import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import { cx, rovingIndex } from './util.ts';
import './SegmentedControl.css';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
  disabled?: boolean;
  describedBy?: string;
}

export interface SegmentedControlProps<T extends string> {
  /** Nombre accesible del grupo («Modo de operación»). */
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'md' | 'lg';
  className?: string;
}

/**
 * Selector de una opción entre pocas (patrón radiogroup): Tab entra en la opción marcada y las
 * flechas, Inicio y Fin cambian la selección.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options.flatMap((option, index) => (option.disabled ? [] : [index]));
  const selected = options.findIndex((o) => o.value === value && !o.disabled);
  const tabStop = selected >= 0 ? selected : enabled[0];

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = rovingIndex(event.key, enabled.indexOf(index), enabled.length);
    if (next === null) return;
    event.preventDefault();
    const target = enabled[next]!;
    onChange(options[target]!.value);
    refs.current[target]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx('af-segmented', `af-segmented--${size}`, className)}
    >
      {options.map((option, i) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={i === tabStop ? 0 : -1}
            disabled={option.disabled}
            aria-describedby={option.describedBy}
            className="af-segmented__option"
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {option.icon && <Icon name={option.icon} />}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
